import { AIProviderService } from '@affine/core/blocksuite/ai/provider/ensure-provider';
import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import { useAIProviderReady } from '@affine/core/components/hooks/affine/use-ai-provider-ready';
import { useFeatureGate } from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { chatPanelOverlayState$ } from './chat-panel-host-registry';
import {
  ActiveViewChatHost,
  WorkspaceCopilotOverlaySurface,
} from './workspace-copilot-overlay';

const Overlay = lazy(() =>
  import('./workspace-chat-sidebar-tab-impl').then(module => ({
    default: module.WorkspaceChatOverlay,
  }))
);
const DetailSync = lazy(() =>
  import('./workspace-chat-sidebar-tab-impl').then(module => ({
    default: module.DetailPageChatSync,
  }))
);
const GraphSync = lazy(() =>
  import('./workspace-chat-sidebar-tab-impl').then(module => ({
    default: module.GraphExplorerChatSync,
  }))
);

export function WorkspaceChatOverlay() {
  const workspaceId = useService(WorkspaceService).workspace.id;
  const overlay = useLiveData(
    useMemo(
      () => LiveData.from(chatPanelOverlayState$(workspaceId), null),
      [workspaceId]
    )
  );
  const [openedWorkspace, setOpenedWorkspace] = useState<string | null>(null);
  const opened = openedWorkspace === workspaceId;
  useEffect(() => {
    if (overlay?.visible) setOpenedWorkspace(workspaceId);
  }, [overlay?.visible, workspaceId]);
  const { isFeatureAllowed } = useFeatureGate();
  const allowed = isFeatureAllowed('managed-ai-credits');
  const ready = useAIProviderReady(opened && allowed);
  const placeholder = (
    <ActiveViewChatHost>
      <WorkspaceCopilotOverlaySurface panel={null} />
    </ActiveViewChatHost>
  );
  return opened && (ready || !allowed) ? (
    <Suspense fallback={placeholder}>
      <Overlay />
    </Suspense>
  ) : (
    placeholder
  );
}

export function DetailPageChatSync(props: {
  editor: AffineEditorContainer | null;
}) {
  const ready = useLiveData(useService(AIProviderService).ready$);
  return ready ? (
    <Suspense fallback={null}>
      <DetailSync {...props} />
    </Suspense>
  ) : null;
}
export function GraphExplorerChatSync() {
  const ready = useLiveData(useService(AIProviderService).ready$);
  return ready ? (
    <Suspense fallback={null}>
      <GraphSync />
    </Suspense>
  ) : null;
}
